import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { Cv, Vacantes as VacantesApi } from "../../../../../api";
import { useNavigate } from "react-router-dom";

const cvController = new Cv();
const vacantesApi = new VacantesApi();

const INPUT = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] outline-none transition-all duration-200 text-gray-900 text-sm";
const SELECT = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#151C84]/30 focus:border-[#151C84] outline-none transition-all duration-200 text-gray-900 text-sm appearance-none";
const LABEL = "block text-sm font-semibold text-gray-700 mb-1.5";

const AREAS_FALLBACK = [
  "Finanzas", "Cuentas por Cobrar", "Gestión Humana",
  "Ejecución / Mercaderistas", "Ventas (Ejecutivo / Supervisor)",
  "Logística", "Almacén", "Tecnología de Información",
  "Comercial (Gerente)", "Mensajería", "Conserjería", "Seguridad / Mecánico",
];

const REDES = [
  { name: "linkedin",  label: "LinkedIn",    placeholder: "https://linkedin.com/in/tu-perfil" },
  { name: "github",    label: "GitHub",      placeholder: "https://github.com/tu-usuario" },
  { name: "instagram", label: "Instagram",   placeholder: "https://instagram.com/tu-usuario" },
  { name: "facebook",  label: "Facebook",    placeholder: "https://facebook.com/tu-perfil" },
  { name: "twitter",   label: "X / Twitter", placeholder: "https://x.com/tu-usuario" },
  { name: "tiktok",    label: "TikTok",      placeholder: "https://tiktok.com/@tu-usuario" },
];

function FormSection({ title, icon, iconBg, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className={`flex items-center gap-3 px-6 py-4 border-b border-gray-100 ${iconBg}`}>
        <div className="w-8 h-8 rounded-lg bg-white/60 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

export function CvForm() {
  const [estado, setEstado] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [vacantes, setVacantes] = useState([]);
  const [redesActivas, setRedesActivas] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    vacantesApi.getAll(true)
      .then((data) => { if (data?.length) setVacantes(data); })
      .catch(() => {});
  }, []);

  const toggleRed = (name) => {
    setRedesActivas((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      if (!next[name]) formik.setFieldValue(name, "");
      return next;
    });
  };

  const formik = useFormik({
    initialValues: {
      nombre: "", apellido: "", documento_identidad: "",
      correo_electronico: "", telefono: "", movil: "",
      genero: "", estado_civil: "", fecha_nacimiento: "",
      licencia_conducir: "", categoria_licencia: "", tiene_vehiculo: "",
      direccion: "", sector_barrio: "", ciudad: "",
      area_interes: "",
      linkedin: "", facebook: "", instagram: "", twitter: "", github: "", tiktok: "",
      fileCv: null,
    },
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        const validar = await cvController.validarDocumento({
          documento_identidad: formValue.documento_identidad,
          correo_electronico: formValue.correo_electronico,
        });
        if (validar.msg !== false) {
          setErrorMsg(validar.msg);
          setShowError(true);
          setTimeout(() => setShowError(false), 5000);
          return;
        }
        await cvController.createCv(formValue);
        setEstado("finalizado");
      } catch (error) {
        setErrorMsg(error.message || "Error al enviar el formulario");
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) {
      setErrorMsg("Solo se permiten archivos PDF o DOCX");
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }
    formik.setFieldValue("fileCv", file);
  };

  if (estado === "finalizado") {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Perfil Registrado!</h2>
        <p className="text-gray-500 mb-2 font-medium">{formik.values.nombre} {formik.values.apellido}</p>
        <p className="text-gray-400 text-sm mb-8 max-w-sm mx-auto">
          Hemos recibido su CV. Nuestro equipo de RRHH revisará su perfil pronto.
        </p>
        <button
          onClick={() => navigate("/recursohumano")}
          className="px-8 py-3 bg-[#151C84] text-white rounded-xl font-bold hover:bg-[#0d125c] transition-all shadow-[0_4px_12px_rgba(21,28,132,0.3)]"
        >
          Volver al Inicio
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-5">
      {/* Header */}
      <div className="text-center mb-2">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Sé Parte de Nuestro Equipo</h2>
        <p className="text-gray-500 text-base max-w-xl mx-auto">
          Completa tus datos y adjunta tu hoja de vida. Evaluaremos tu perfil para futuras oportunidades.
        </p>
      </div>

      {showError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-3">
          <svg className="w-5 h-5 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="font-medium text-sm">{errorMsg}</span>
        </div>
      )}

      {/* Área de Interés */}
      <FormSection
        title="Área de Interés"
        iconBg="bg-[#C81416]/5"
        icon={<svg className="w-4 h-4 text-[#C81416]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
      >
        <div>
          <label className={LABEL}>Puesto / Área al que deseas aplicar <span className="text-[#C81416]">*</span></label>
          <div className="relative">
            <select
              name="area_interes"
              value={formik.values.area_interes}
              onChange={formik.handleChange}
              required
              className={SELECT}
            >
              <option value="">— Selecciona un área —</option>
              {vacantes.map((v) => (
                <option key={v.id} value={v.titulo}>
                  {v.titulo}{v.departamento ? ` — ${v.departamento}` : ""}
                </option>
              ))}
              {AREAS_FALLBACK.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </FormSection>

      {/* Información Personal */}
      <FormSection
        title="Información Personal"
        iconBg="bg-[#151C84]/4"
        icon={<svg className="w-4 h-4 text-[#151C84]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={LABEL}>Nombre *</label>
            <input type="text" name="nombre" value={formik.values.nombre} onChange={formik.handleChange} required className={INPUT} placeholder="Tu nombre" /></div>
          <div><label className={LABEL}>Apellido *</label>
            <input type="text" name="apellido" value={formik.values.apellido} onChange={formik.handleChange} required className={INPUT} placeholder="Tu apellido" /></div>
          <div><label className={LABEL}>Cédula de Identidad *</label>
            <input type="text" name="documento_identidad" value={formik.values.documento_identidad} onChange={formik.handleChange} required className={INPUT} placeholder="000-0000000-0" /></div>
          <div><label className={LABEL}>Correo Electrónico *</label>
            <input type="email" name="correo_electronico" value={formik.values.correo_electronico} onChange={formik.handleChange} required className={INPUT} placeholder="correo@ejemplo.com" /></div>
          <div><label className={LABEL}>Teléfono</label>
            <input type="text" name="telefono" value={formik.values.telefono} onChange={formik.handleChange} className={INPUT} placeholder="809-000-0000" /></div>
          <div><label className={LABEL}>Móvil</label>
            <input type="text" name="movil" value={formik.values.movil} onChange={formik.handleChange} className={INPUT} placeholder="829-000-0000" /></div>
          <div><label className={LABEL}>Género</label>
            <select name="genero" value={formik.values.genero} onChange={formik.handleChange} className={SELECT}>
              <option value="">Seleccionar</option>
              <option value="Masculino">Masculino</option>
              <option value="Femenino">Femenino</option>
            </select></div>
          <div><label className={LABEL}>Estado Civil</label>
            <select name="estado_civil" value={formik.values.estado_civil} onChange={formik.handleChange} className={SELECT}>
              <option value="">Seleccionar</option>
              <option value="soltero">Soltero/a</option>
              <option value="casado">Casado/a</option>
              <option value="union_libre">Unión Libre</option>
              <option value="viudo">Viudo/a</option>
            </select></div>
          <div className="sm:col-span-2"><label className={LABEL}>Fecha de Nacimiento</label>
            <input type="date" name="fecha_nacimiento" value={formik.values.fecha_nacimiento} onChange={formik.handleChange} className={`${INPUT} sm:max-w-xs`} /></div>
        </div>
      </FormSection>

      {/* Licencia y Vehículo */}
      <FormSection
        title="Licencia y Vehículo"
        iconBg="bg-[#C81416]/5"
        icon={<svg className="w-4 h-4 text-[#C81416]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={LABEL}>Licencia de Conducir</label>
            <select name="licencia_conducir" value={formik.values.licencia_conducir}
              onChange={(e) => { formik.setFieldValue("licencia_conducir", e.target.value); if (e.target.value === "no") formik.setFieldValue("categoria_licencia", ""); }}
              className={SELECT}>
              <option value="">Seleccionar</option>
              <option value="no">No</option>
              <option value="2">Cat 2</option>
              <option value="3">Cat 3</option>
              <option value="4">Cat 4</option>
            </select></div>
          <div><label className={LABEL}>Tiene Vehículo Propio</label>
            <select name="tiene_vehiculo" value={formik.values.tiene_vehiculo} onChange={formik.handleChange} className={SELECT}>
              <option value="">Seleccionar</option>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select></div>
        </div>
      </FormSection>

      {/* Ubicación */}
      <FormSection
        title="Ubicación"
        iconBg="bg-[#151C84]/5"
        icon={<svg className="w-4 h-4 text-[#151C84]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={LABEL}>Dirección Completa</label>
            <input type="text" name="direccion" value={formik.values.direccion} onChange={formik.handleChange} className={INPUT} placeholder="Calle, Número, Referencia" /></div>
          <div><label className={LABEL}>Sector / Barrio</label>
            <input type="text" name="sector_barrio" value={formik.values.sector_barrio} onChange={formik.handleChange} className={INPUT} placeholder="Ej. Piantini" /></div>
          <div><label className={LABEL}>Ciudad</label>
            <input type="text" name="ciudad" value={formik.values.ciudad} onChange={formik.handleChange} className={INPUT} placeholder="Ej. Santo Domingo" /></div>
        </div>
      </FormSection>

      {/* Redes Sociales — selección por checkboxes */}
      <FormSection
        title="Redes Sociales (Opcional)"
        iconBg="bg-[#151C84]/5"
        icon={<svg className="w-4 h-4 text-[#151C84]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>}
      >
        <p className="text-xs text-gray-400 mb-4">
          Selecciona las redes que tengas y proporciona el enlace. Son opcionales pero ayudan a complementar tu perfil.
        </p>

        {/* Pill toggles */}
        <div className="flex flex-wrap gap-2 mb-5">
          {REDES.map(({ name, label }) => (
            <button
              key={name}
              type="button"
              onClick={() => toggleRed(name)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                redesActivas[name]
                  ? "bg-[#151C84] text-white border-[#151C84]"
                  : "bg-gray-50 text-gray-600 border-gray-200 hover:border-[#151C84]/40 hover:text-[#151C84]"
              }`}
            >
              {redesActivas[name] ? "✓ " : ""}{label}
            </button>
          ))}
        </div>

        {/* Inputs solo de las redes seleccionadas */}
        {REDES.some(({ name }) => redesActivas[name]) ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {REDES.filter(({ name }) => redesActivas[name]).map(({ name, label, placeholder }) => (
              <div key={name}>
                <label className={LABEL}>{label}</label>
                <input
                  type="url"
                  name={name}
                  value={formik.values[name]}
                  onChange={formik.handleChange}
                  className={INPUT}
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">Ninguna seleccionada.</p>
        )}
      </FormSection>

      {/* Adjuntar CV */}
      <div className="bg-[#151C84]/4 border-2 border-dashed border-[#151C84]/25 hover:border-[#151C84]/50 rounded-2xl p-8 text-center transition-colors">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-sm mb-4">
          <svg className="w-7 h-7 text-[#151C84]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">Adjunta tu Currículum</h3>
        <p className="text-sm text-gray-400 mb-5">PDF o DOCX — Máx. 5MB</p>
        <label className="inline-flex cursor-pointer items-center justify-center px-6 py-2.5 text-sm font-semibold text-white bg-[#151C84] rounded-xl hover:bg-[#0d125c] transition-all">
          <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={handleFileChange} />
          Seleccionar Archivo
        </label>
        {formik.values.fileCv && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 px-4 py-2 rounded-xl">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="truncate max-w-[200px]">{formik.values.fileCv.name}</span>
          </div>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={formik.isSubmitting}
        className="w-full flex items-center justify-center gap-3 py-4 bg-[#151C84] hover:bg-[#0d125c] text-white text-base font-bold rounded-xl transition-all shadow-[0_4px_14px_rgba(21,28,132,0.3)] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {formik.isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Procesando...
          </>
        ) : (
          <>
            Enviar Solicitud
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </>
        )}
      </button>
    </form>
  );
}
